import React from 'react';
import { 
  Typography, 
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  styled
} from '@mui/material';

interface PricingGroup {
  hourLimit: number;
  description: string;
  pricings: any[];
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: '#fff',
  fontSize: '0.8rem',
  padding: theme.spacing(1),
  borderBottom: '1px solid #333',
}));

const StyledTableHeadCell = styled(StyledTableCell)(({ theme }) => ({
  backgroundColor: '#333',
  fontWeight: 'bold',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
}));

interface LocalPricingDisplayProps {
  group: PricingGroup;
}

const LocalPricingDisplay: React.FC<LocalPricingDisplayProps> = ({ group }) => {
  // For Pune Local services, we want to hide the offer price column
  const showOfferPrice = false;
  
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
        {group.description}
      </Typography>
      <TableContainer component={Paper} sx={{ backgroundColor: '#2a2a2a' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <StyledTableHeadCell>Car Type</StyledTableHeadCell>
              <StyledTableHeadCell>Make & Model</StyledTableHeadCell>
              <StyledTableHeadCell>Capacity</StyledTableHeadCell>
              {/* <StyledTableHeadCell>Fuel Type</StyledTableHeadCell> */}
              <StyledTableHeadCell align="right"> Price (₹)</StyledTableHeadCell>
              {showOfferPrice && <StyledTableHeadCell align="right" sx={{ color: '#4caf50' }}>Offer Price (₹)</StyledTableHeadCell>}
              <StyledTableHeadCell align="right">Minimum Price (₹)</StyledTableHeadCell>
              <StyledTableHeadCell align="right">Rate/KM (₹)</StyledTableHeadCell>
              <StyledTableHeadCell align="right">Rate/Extra Hour (₹)</StyledTableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {group.pricings.map((pricing) => (
              <StyledTableRow key={pricing.carTypeId}>
                <StyledTableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                      color: (() => {
                        // Assign different colors based on car type
                        const carType = pricing.carType.toLowerCase();
                        if (carType.includes('sedan')) return '#42a5f5'; // Blue for Sedan
                        if (carType.includes('suv')) return '#ffb74d'; // Orange for SUV
                        if (carType.includes('premium')) return '#ba68c8'; // Purple for Premium
                        if (carType.includes('luxury')) return '#ffd54f'; // Yellow for Luxury
                        if (carType.includes('hatchback')) return '#4db6ac'; // Teal for Hatchback
                        return '#e0e0e0'; // Default light gray
                      })(),
                      backgroundColor: (() => {
                        // Assign different background colors based on car type
                        const carType = pricing.carType.toLowerCase();
                        if (carType.includes('sedan')) return 'rgba(66, 165, 245, 0.15)'; // Light blue bg
                        if (carType.includes('suv')) return 'rgba(255, 183, 77, 0.15)'; // Light orange bg
                        if (carType.includes('premium')) return 'rgba(186, 104, 200, 0.15)'; // Light purple bg
                        if (carType.includes('luxury')) return 'rgba(255, 213, 79, 0.15)'; // Light yellow bg
                        if (carType.includes('hatchback')) return 'rgba(77, 182, 172, 0.15)'; // Light teal bg
                        return 'transparent'; // Default transparent
                      })(),
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      display: 'inline-block'
                    }}
                  >
                    {pricing.carType}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>{pricing.makeCompany} {pricing.model}</StyledTableCell>
                <StyledTableCell>{pricing.capacity - 1}</StyledTableCell>
                {/* <StyledTableCell>{pricing.fuelType}</StyledTableCell> */}
                <StyledTableCell align="right">
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {pricing.basePrice.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </Typography>
                </StyledTableCell>
                {showOfferPrice && (
                  <StyledTableCell align="right">
                    {pricing.offerInfo ? (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'flex-end',
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        borderRadius: 1,
                        p: 0.75,
                        border: '1px dashed rgba(76, 175, 80, 0.3)'
                      }}>
                        <Typography variant="body2" sx={{ 
                          color: '#4caf50', 
                          fontWeight: 'bold',
                          fontSize: '0.9rem'
                        }}>
                          {pricing.offerInfo.discountedPrice.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </Typography>
                        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ 
                            backgroundColor: '#4caf50', 
                            color: '#fff', 
                            fontSize: '0.65rem', 
                            fontWeight: 'bold',
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            OFFER
                          </Box>
                          <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 'medium' }}>
                            {pricing.offerInfo.name}
                          </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ 
                          color: '#4caf50', 
                          fontWeight: 'bold',
                          backgroundColor: 'rgba(76, 175, 80, 0.1)',
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 0.5,
                          mt: 0.5
                        }}>
                          {/* SAVE {((pricing.basePrice - pricing.offerInfo.discountedPrice) / pricing.basePrice * 100).toFixed(0)}% */}
                        </Typography>
                      </Box>
                    ) : (
                      '-'
                    )}
                  </StyledTableCell>
                )}
                <StyledTableCell align="right">
                  {pricing.negotiationPrice ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <Typography variant="body2" sx={{ color: '#ff9800', fontWeight: 'bold' }}>
                        {pricing.negotiationPrice.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </Typography>
                      {pricing.basePrice > pricing.negotiationPrice && (
                        <Typography variant="caption" sx={{ 
                          color: '#4caf50', 
                          fontWeight: 'bold',
                          backgroundColor: 'rgba(76, 175, 80, 0.1)',
                          // px: 0.75,
                          // py: 0.25,
                          // borderRadius: 0.5,
                          mt: 0.5
                        }}>
                          {/* SAVE {((pricing.basePrice - pricing.negotiationPrice) / pricing.basePrice * 100).toFixed(0)}% */}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    '-'
                  )}
                </StyledTableCell>
                <StyledTableCell align="right">{pricing.pricePerKm || '-'}</StyledTableCell>
                <StyledTableCell align="right">{pricing.ratePerExtraHour || '-'}</StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default LocalPricingDisplay;
